import {IReg, REG} from "/@lib@/commons/registry.js";
import {WedMgr} from "/@back@/edit/wed/wedEditor.js";
import {DOMSH} from "/@lib@/commons/xml/domsh.js";
import {ISearchBarPanel, JWedSearchBarParams, SearchBarPanelAction, SearchPanelBase, WedSearchBar} from "/@back@/edit/wed/features/searchBar.js";
import {srcRef} from "/@lib@/wsp/src.js";
import {SrcPointer, SrcPointerErase, SrcPointerFastSelect, SrcPointerPaste} from "/@back@/wsp/widgets/srcDrawer.js";
import {IWspUiEnv} from "/@lib@/wsp/wsp.js";
import {SkCommentSearch, SkItemSearch} from "/@back@/wsp/widgets/wed/schemaMetaWsp.js";
import {Action} from "/@lib@/commons/actions.js";
import {IShortDescCtx} from "/@lib@/wsp/item.js";
import {JSX} from "/@lib@/commons/xml/dom.js";
import {Button} from "/@back@/commons/widgets/buttons.js";
import {ISkSearchAnnot, isSkSearchAnnot} from "/@lib@/edit/schema/schemaSearch.js";
import {ISkAnnot} from "/@lib@/edit/schema/schemaAnnots.js";
import {EWedletEditMode, WEDLET} from "/@back@/edit/wed/wedlets/wedlet.js";
import {POPUP} from "/@back@/commons/widgets/popups.js";
import {XA} from "/@lib@/commons/xml/xAddr.js";

/** Barre de parcours des liens vers des items. */
class ItemSearchAction extends SearchBarPanelAction {

	constructor(id?: string, maxResults?: number) {
		super(id || 'item');
		this.maxResults = maxResults || 300;
		this._icon = "/@skin@/edit/wed/search/linkSearch.svg";
		this._label = "Parcourir les liens";
		this._description = "Chercher les liens vers les items";
	}

	buildPanel(ctx: WedSearchBar, ev?: Event): ISearchBarPanel {
		return new ItemSearchPanel().initPanel(this, ctx);
	}
}

REG.reg.addToList("wed.searchBar.actions", "item", 1, new ItemSearchAction(), 50);


export interface JItemWedSearchBarParams extends JWedSearchBarParams {
	findRef?: srcRef
	repRef?: srcRef
}

/** */
class ItemSearchPanel extends SearchPanelBase {
	action: ItemSearchAction;
	reg: IReg<IWspUiEnv>;
	findPtr: SrcPointer;
	repPtr: SrcPointer;
	repBtn: Button;
	repAllBtn: Button;

	initPanel(action: ItemSearchAction, bar: WedSearchBar): this {
		this._initPanel(action, bar, true);
		this.findPtr = new SrcPointer().initialize({
			reg: this.reg,
			defaultAction: SrcPointerFastSelect.SINGLETON,
			actionsLists: ["actions:wsp:wed:itemSearch", "actions:wsp:shortDesc"],
			accelKeysLists: ["accelkeys:wsp:wed:itemSearch", "accelkeys:wsp:shortDesc"],
			draggable: true,
			dropable: true
		});
		this.findPtr.title = "Lien vers un item particulier";
		this.findPtr.onSrcRefChange.add(this.onChange);
		this.searchBar.append(
			this.findPtr,
			this.nav,
			this.result
		);
		return this;
	}

	setSearch(params: JItemWedSearchBarParams): void {
		if (params.findRef != null) this.findPtr.setSrcRef(params.findRef);
		this.execSearch();
		if (params.repRef !== undefined) {
			this.showReplaceBar(true);
			this.repPtr.setSrcRef(params.repRef);
		}
	}

	buildSearch(): JItemWedSearchBarParams {
		return {
			searchId: this.action.getId(),
			findRef: this.findPtr.srcRef,
			repRef: this.replaceBarShown ? this.repPtr.srcRef : undefined
		}
	}


	protected buildReplaceBar() {
		this.repPtr = new SrcPointer().initialize({
			reg: this.reg,
			defaultAction: SrcPointerFastSelect.SINGLETON,
			actionsLists: ["actions:wsp:wed:itemSearch", "actions:wsp:shortDesc"],
			accelKeysLists: ["accelkeys:wsp:wed:itemSearch", "accelkeys:wsp:shortDesc"],
			draggable: true,
			dropable: true
		});
		this.repPtr.title = "Remplacer par cet item";
		this.repPtr.onSrcRefChange.add(this.onRepChange);
		this.repBtn = <Button label="Remplacer" onclick={this.onReplace} disabled/> as Button;
		this.repAllBtn = <Button label="Tout remplacer" onclick={this.onReplaceAll} disabled/> as Button;
		this.replaceBar.append(
			this.repPtr,
			this.repBtn,
			this.repAllBtn
		);
	}

	protected onChange(this: void, srcPtr: SrcPointer) {
		const me = DOMSH.findHost<ItemSearchPanel>(srcPtr);
		me.execSearch();
		DOMSH.findHost<WedSearchBar>(me).dispatchSearchChange();
	}

	protected onRepChange(this: void, srcPtr: SrcPointer) {
		const me = DOMSH.findHost<ItemSearchPanel>(srcPtr);
		const disabled = !srcPtr.srcRef;
		me.repBtn.disabled = disabled;
		me.repAllBtn.disabled = disabled;
	}

	protected onReplace(this: HTMLInputElement) {
		const me = DOMSH.findHost<ItemSearchPanel>(this);
		const repBy = me.repPtr.srcRef;
		if (!repBy) return;
		const annot = me.nav.getLastSelectedAnnot();
		if (annot) {
			const wedMgr = me.wedMgr;
			const start = XA.freeze(annot.start);
			const startWedlet = WEDLET.findWedlet(wedMgr.rootWedlet, start, {lastAncestorIfNone: true});
			if (startWedlet && WEDLET.resolveEditMode(startWedlet) === EWedletEditMode.write) {
				const batch = me.wedMgr.docHolder.newBatch(me.wedMgr.getCurrentSel());
				batch.setAttr(start, repBy);
				batch.setSelAfter(start);
				batch.doBatch();
			} else {
				POPUP.showNotifForbidden("Contenu non modifiable", this, null, {viewPortX: "middle", viewPortY: "1/3"});
			}
		}
		me.nav.gotoAnnot(1);
	}

	protected onReplaceAll(this: HTMLInputElement) {
		const me = DOMSH.findHost<ItemSearchPanel>(this);
		const repBy = me.repPtr.srcRef;
		if (!repBy) return;
		const wedMgr = me.wedMgr;
		const batch = wedMgr.docHolder.newBatch(wedMgr.getCurrentSel());
		const annots = wedMgr.lastSkAnnots;
		let lastAn: ISkSearchAnnot;
		for (let i = annots.length - 1; i >= 0; i--) {
			const an: ISkAnnot = annots[i];
			if (isSkSearchAnnot(an)) {
				const start = XA.freeze(an.start);
				const startWedlet = WEDLET.findWedlet(wedMgr.rootWedlet, start, {lastAncestorIfNone: true});
				//FIXME perms configuré sur wedlets, mais wedlets peut être non construit, passer perms sur schéma...
				if (startWedlet && WEDLET.resolveEditMode(startWedlet) === EWedletEditMode.write) batch.setAttr(start, repBy);
				if (!lastAn) lastAn = an;
			}
		}
		if (lastAn) {
			batch.setSelAfter(lastAn.start);
			batch.doBatch();
		} else {
			me.nav.gotoAnnot(1);
		}
	}

	execSearch() {
		if (!this.wedMgr) return;
		if (this.wedMgr.docHolder.setSkSearch(new SkItemSearch(this.findPtr.srcRef, this.action.maxResults)).length === 0) {
			this.updateCount(0);
		}
	}

	connectDoc(wedMgr: WedMgr): void {
		super.connectDoc(wedMgr);
		this.findPtr.focus();
	}
}

function addAction(action: Action<IShortDescCtx>, accel?: string) {
	REG.reg.addToList("actions:wsp:wed:itemSearch", action.getId(), 1, action);
	if (accel) REG.reg.addToList("accelkeys:wsp:wed:itemSearch", accel, 1, action);
}

addAction(SrcPointerFastSelect.SINGLETON, "Enter");
addAction(SrcPointerPaste.SINGLETON, "v-accel");
addAction(new SrcPointerErase(), "Delete");

customElements.define("wed-searchbar-item", ItemSearchPanel);


/**
 * Parcours des commentaires.
 */
class CommentSearchAction extends SearchBarPanelAction {

	constructor(id?: string, maxResults?: number) {
		super(id || 'comment');
		this.maxResults = maxResults || 300;
		this._icon = "/@skin@/edit/wed/search/commentSearch.svg";
		this._label = "Parcourir les commentaires";
	}

	buildPanel(ctx: WedSearchBar, ev?: Event): ISearchBarPanel {
		return new CommentSearchPanel().initPanel(this, ctx);
	}
}

REG.reg.addToList("wed.searchBar.actions", "comment", 1, new CommentSearchAction(), 100);

export interface JCommentWedSearchBarParams extends JWedSearchBarParams {
	opened?: boolean
	closed?: boolean
}

/** */
class CommentSearchPanel extends SearchPanelBase {
	action: CommentSearchAction;

	opened: HTMLInputElement;
	closed: HTMLInputElement;

	initPanel(action: CommentSearchAction, bar: WedSearchBar): this {
		this._initPanel(action, bar, false);
		this.opened = <input type="checkbox" onchange={this.onChange} checked=""/> as HTMLInputElement;
		this.closed = <input type="checkbox" onchange={this.onChange} checked=""/> as HTMLInputElement;
		this.shadowRoot.append(
			this.nav,
			<div class="options">
				<label>{this.opened}Ouverts</label>
				<label>{this.closed}Clos</label>
			</div>,
			this.result
		);
		return this;
	}

	setSearch(params: JCommentWedSearchBarParams): void {
		if (typeof params.opened === "boolean") this.opened.checked = params.opened;
		if (typeof params.closed === "boolean") this.closed.checked = params.closed;
		this.execSearch();
	}

	buildSearch(): JCommentWedSearchBarParams {
		return {
			searchId: this.action.getId(),
			opened: this.opened.checked,
			closed: this.closed.checked
		}
	}

	protected onChange(this: HTMLInputElement) {
		const panel = DOMSH.findHost<CommentSearchPanel>(this);
		if (!panel.opened.checked && !panel.closed.checked) {
			//Les deux ne peuvent être décochés à la fois, on récative celui qui a été décoché.
			if (this === panel.opened) {
				panel.closed.checked = true;
			} else {
				panel.opened.checked = true;
			}
		}
		panel.execSearch();
		DOMSH.findHost<WedSearchBar>(panel).dispatchSearchChange();
	}

	execSearch() {
		if (!this.wedMgr) return;
		if (this.wedMgr.docHolder.setSkSearch(new SkCommentSearch(this.opened.checked, this.closed.checked, this.action.maxResults)).length === 0) {
			this.updateCount(0);
		}
	}
}

customElements.define("wed-searchbar-comment", CommentSearchPanel);

